import { useEffect, useState, useCallback } from "react";
import { api, API } from "@/lib/api";
import { dateIt } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { FileDown, Upload, Trash2, Receipt, FileSignature, Package, FileText } from "lucide-react";

const CATEGORIES = [
  { value: "fattura", label: "Pro-forma", icon: Receipt, color: "bg-blue-100 text-blue-700" },
  { value: "contratto", label: "Contratto", icon: FileSignature, color: "bg-amber-100 text-amber-700" },
  { value: "altro", label: "Altro", icon: Package, color: "bg-slate-100 text-slate-700" },
];
const catMeta = (c) => CATEGORIES.find((x) => x.value === c) || CATEGORIES[CATEGORIES.length - 1];

export default function AdminDocuments() {
  const [docs, setDocs] = useState([]);
  const [partners, setPartners] = useState([]);
  const [filterPartner, setFilterPartner] = useState("all");
  const [filterCat, setFilterCat] = useState("all");
  const [upPartner, setUpPartner] = useState("");
  const [upCategory, setUpCategory] = useState("fattura");
  const [upTitle, setUpTitle] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (filterPartner !== "all") params.partner_id = filterPartner;
    if (filterCat !== "all") params.category = filterCat;
    api.get("/documents", { params }).then(r => setDocs(r.data));
  }, [filterPartner, filterCat]);

  useEffect(() => {
    api.get("/partners").then(r => setPartners(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!upPartner) { toast.error("Seleziona un partner"); e.target.value = ""; return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/documents/upload", fd, {
        params: { partner_id: upPartner, category: upCategory, title: upTitle || file.name },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Documento caricato · partner notificato via email");
      setUpTitle(""); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore upload");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare il documento?")) return;
    await api.delete(`/documents/${id}`);
    toast.success("Documento eliminato");
    load();
  };

  const sizeKb = (b) => `${(b / 1024).toFixed(1)} KB`;

  return (
    <div className="space-y-6" data-testid="admin-documents">
      <div>
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Area documentale</h1>
        <p className="text-slate-500 mt-1">Carica fatture, attestati e contratti per i partner. Il partner viene notificato via email.</p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-5 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Partner destinatario</Label>
            <Select value={upPartner} onValueChange={setUpPartner}>
              <SelectTrigger data-testid="admin-doc-partner"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
              <SelectContent>
                {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.company_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Categoria</Label>
            <Select value={upCategory} onValueChange={setUpCategory}>
              <SelectTrigger data-testid="admin-doc-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-1">
            <Label className="text-xs">Titolo documento</Label>
            <Input value={upTitle} onChange={(e) => setUpTitle(e.target.value)} placeholder="Pro-forma 2026-001" data-testid="admin-doc-title" />
          </div>
          <div className="flex items-end">
            <label className="w-full">
              <input type="file" className="hidden" onChange={handleUpload} data-testid="admin-doc-file" />
              <Button asChild disabled={uploading || !upPartner} className="w-full bg-[#0F2942] hover:bg-[#1B3B5F]">
                <span><Upload className="w-4 h-4 mr-1" /> {uploading ? "Caricamento…" : "Carica"}</span>
              </Button>
            </label>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2 flex-wrap">
        <Select value={filterPartner} onValueChange={setFilterPartner}>
          <SelectTrigger className="w-56" data-testid="admin-doc-filter-partner"><SelectValue placeholder="Partner" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i partner</SelectItem>
            {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.company_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-44" data-testid="admin-doc-filter-cat"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-slate-200"><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Titolo</TableHead><TableHead>Partner</TableHead>
            <TableHead>Categoria</TableHead><TableHead>File</TableHead>
            <TableHead>Dim.</TableHead><TableHead>Caricato</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {docs.map((d) => {
              const m = catMeta(d.category);
              return (
                <TableRow key={d.id} data-testid={`admin-doc-row-${d.id}`}>
                  <TableCell className="font-medium">{d.title}</TableCell>
                  <TableCell>{d.partner_name}</TableCell>
                  <TableCell><span className={`status-pill ${m.color}`}><m.icon className="w-3 h-3" />{m.label}</span></TableCell>
                  <TableCell className="text-xs text-slate-500 truncate max-w-[180px]">{d.original_filename}</TableCell>
                  <TableCell className="text-xs">{sizeKb(d.size || 0)}</TableCell>
                  <TableCell className="text-xs">{dateIt(d.created_at)}</TableCell>
                  <TableCell className="flex gap-1">
                    <a href={`${API}/documents/${d.id}/download`} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm" data-testid={`admin-doc-dl-${d.id}`}><FileDown className="w-4 h-4" /></Button>
                    </a>
                    <Button variant="ghost" size="icon" onClick={() => del(d.id)} data-testid={`admin-doc-del-${d.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {docs.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-12">
                <FileText className="w-10 h-10 mx-auto text-slate-300" />
                <div className="mt-2">Nessun documento caricato.</div>
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
